'use client'

import { usePathname } from 'next/navigation'
import { AnimatePresence } from 'motion/react'
import Pill from '@/components/ui/Pill'
import Toast from '@/components/ui/Toast'
import Breadcrumb from '@/components/content/sidebar/Breadcrumb'
import Menu from '@/components/ui/Menu'
import { useShareLink } from '@/hooks/useShareLink'
import { generateMenu } from '@/helpers/menus'
import type { Blog, App } from '@/payload-types'

type BlogPost = Pick<
  Blog,
  | 'id'
  | 'name'
  | 'slug'
  | 'blogApps'
  | 'blogCategories'
  | 'post'
  | 'isFeatured'
  | 'summary'
  | 'authors'
>

type BlogPostOverviewProps = {
  blog: BlogPost
  apps: App[]
}

export default function BlogPostOverview({ blog, apps }: BlogPostOverviewProps) {
  const pathname = usePathname()
  const { copied, copyLink } = useShareLink()
  const menu = generateMenu('app', { app: apps, pathname })

  return (
    <>
      <div className="flex w-full flex-row items-center gap-3.5 md:flex-col md:items-start md:p-4 lg:flex-row lg:items-center">
        <div className="flex w-full min-w-0 flex-1 items-center gap-3.5 lg:w-[calc(100%-124px)]">
          <div className="mt-1 hidden w-full flex-col gap-0.5 leading-tight md:flex">
            <span className="truncate font-semibold text-gray-950 dark:text-white">
              {blog.name}
            </span>
            {/* TODO - calculate reading time */}
            <span className="truncate text-13 text-gray-600 dark:text-zinc-500">
              2 min read,{' '}
              <span className="text-gray-400">
                {apps.length} app{apps.length === 1 ? '' : 's'} stack
              </span>
            </span>
          </div>
          <Breadcrumb placeholder="Blog" breakpoint="md" />
        </div>
        <div className="flex shrink-0 gap-2.5 lg:ml-auto">
          <Pill style="primary" onClick={() => copyLink()}>
            Share
          </Pill>

          {menu.length > 0 && <Menu items={menu} subAlign="down" />}
        </div>
      </div>
      <AnimatePresence>{copied && <Toast>Link copied to clipboard</Toast>}</AnimatePresence>
    </>
  )
}
